import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {AvatarStack} from '@primer/react'
import {useMemo} from 'react'

import type {IAssignee, Review} from '../../../api/common-contracts'
import {isCopilot} from '../../../helpers/assignee-helper'
import {joinOxford} from '../../../helpers/join-oxford'
import {TextCell} from './text-cell'
import styles from './user-group.module.css'

export function itemFromReview(review: Review): UserGroupItem {
  return {
    ...review.reviewer,
    type: review.reviewer.type === 'Team' ? 'team' : review.reviewer.type === 'Bot' ? 'bot' : 'user',
    hovercardUrl:
      review.reviewer.type === 'User'
        ? `/hovercards?user_id=${review.reviewer.id}`
        : `${review.reviewer.url}/hovercard`,
  }
}

export function itemFromAssignee(assignee: IAssignee): UserGroupItem {
  return {...assignee, name: assignee.login}
}

export type UserGroupItem = {
  id: number
  avatarUrl: string
  name: string
  type?: 'user' | 'team' | 'bot'
  hovercardUrl?: string
}

interface AvatarWithLoginProps {
  user: UserGroupItem
  isDisabled?: boolean
}

// Sort users in order of:
// 1) users
// 2) teams
// 3) bots
const typeOrder = (type: string | undefined) => {
  switch (type) {
    case 'user':
      return 0
    case 'team':
      return 1
    case 'bot':
      return 2
    default:
      return 3
  }
}

const AvatarWithLogin: React.FC<AvatarWithLoginProps> = ({user, isDisabled}) => {
  return (
    <div className={styles.Box}>
      {isCopilot(user.name) ? (
        <CopilotAvatar className={styles.GitHubAvatar} />
      ) : (
        <GitHubAvatar
          loading="lazy"
          alt={user.name}
          src={user.avatarUrl}
          data-hovercard-url={user.hovercardUrl}
          className={styles.GitHubAvatar}
        />
      )}
      <TextCell isDisabled={isDisabled} className={styles.TextCell}>
        {user.name}
      </TextCell>
    </div>
  )
}

interface UserGroupProps {
  users: Array<UserGroupItem> | undefined
  isDisabled?: boolean
  /**
   * How to sort users when displaying them. Default: `unsorted`.
   *
   * * `unsorted`: Display users in the order they are provided.
   * * `users-first`: Display individual users first, then teams.
   */
  sortMode?: 'unsorted' | 'users-first'
}

export const UserGroup: React.FC<UserGroupProps> = ({users, isDisabled, sortMode = 'unsorted'}) => {
  const sortedUsers = useMemo(() => {
    switch (sortMode) {
      case 'users-first': {
        // Slice first to obtain a new copy of the array, then sort the new array in-place and return it.
        return users?.slice().sort((a, b) => {
          return typeOrder(a.type) - typeOrder(b.type)
        })
      }
      case 'unsorted':
      default:
        return users
    }
  }, [users, sortMode])

  if (!sortedUsers) {
    return null
  }

  if (sortedUsers.length > 1) {
    return (
      <>
        <AvatarStack className={styles.AvatarStack}>
          {sortedUsers.map(user =>
            isCopilot(user.name) ? (
              <CopilotAvatar key={user.id} />
            ) : (
              <GitHubAvatar
                loading="lazy"
                key={user.id}
                alt={user.name}
                src={user.avatarUrl}
                data-hovercard-url={user.hovercardUrl}
              />
            ),
          )}
        </AvatarStack>
        <TextCell isDisabled={isDisabled} className={styles.TextCell_1}>
          {joinOxford(sortedUsers.map(user => user.name))}
        </TextCell>
      </>
    )
  }

  return (
    <>
      {sortedUsers.map(user => (
        <AvatarWithLogin key={user.id} user={user} isDisabled={isDisabled} />
      ))}
    </>
  )
}

try{ AvatarWithLogin.displayName ||= 'AvatarWithLogin' } catch {}
try{ UserGroup.displayName ||= 'UserGroup' } catch {}